import type { GuidedSetupStep } from '../components/guided-setup/guided-setup-steps';

/**
 * Guided-tour steps for the **Remediation policy** page, `Settings -> Remediation policy`
 * (`docs/features/remediation-policy.md`).
 *
 * Every `anchor` is a `data-tour-step` attribute value on a real element of
 * `RemediationPolicySettingsComponent`; a step whose anchor is absent (the panel renders nothing
 * without `remediation.policy.read`) is skipped, so only the
 * intro renders in that case.
 *
 * Copy discipline: the policy creates plans; a draft pull request only under the separately granted
 * automatic pull request authority (2026-10-08). No step implies a merge.
 * The tour is host-owned (`GuidedTourHostComponent`), whose permanent "Show me around" trigger is
 * the on-demand re-open path (`.claude/rules/new-section-onboarding-coverage.md` R2).
 */

/** Tour id — must appear in `UserTourController.ALLOWED_TOUR_IDS` (<= 32 chars). */
export const REMEDIATION_POLICY_TOUR_ID = 'remediation-policy';

export const REMEDIATION_POLICY_TOUR_STEPS: readonly GuidedSetupStep[] = [
  {
    id: 'rp-intro',
    title: 'Plans that start themselves',
    anchor: null,
    body: [
      'A remediation policy creates remediation plans automatically after a completed SAST or SCA ' +
        'scan, without anyone approving each plan. A draft pull request needs a separate, expiring ' +
        'authority and an independently proven fix. It never merges.',
      'A new policy starts disabled. Nothing runs until an organization administrator enables it ' +
        'and separately grants it the right to create plans.',
    ],
  },
  {
    id: 'rp-triggers',
    title: 'Which scans it reacts to',
    anchor: 'rp-triggers',
    body: [
      'Only scans whose origin the platform can prove are selectable: a manual scan and a ' +
        'scheduled scan. Other triggers are listed but disabled, each with the reason it cannot ' +
        'run yet.',
    ],
  },
  {
    id: 'rp-rule',
    title: 'Which findings get a plan',
    anchor: 'rp-rule',
    body: [
      'Rules match finding class and severity, and can skip triaged findings or require a known ' +
        'fixed version. A value the platform cannot compute never counts as a match.',
      'Limits cap plans per scan, per day, in progress, and daily spend. Budget is reserved before ' +
        'any plan starts.',
    ],
  },
  {
    id: 'rp-grant',
    title: 'The grant, and revoking it',
    anchor: 'rp-grant',
    body: [
      'Enabling a policy does not let it spend. An administrator grants plan creation explicitly; ' +
        'revoking stops new plans at once, and running plans stop before their next model turn.',
      'If the administrator who granted it loses that role, or the rules change, the grant stops until ' +
        'a current administrator grants it again. "Stop all policies" stops every scope at once, ' +
        'including workspace and repository overrides.',
    ],
  },
  {
    id: 'rp-history',
    title: 'What it decided, and why',
    anchor: 'rp-history',
    body: [
      'Every scan the policy saw is recorded with its outcome: evaluated, or refused with a named ' +
        'reason. Preview runs the same evaluation against a completed scan without creating anything.',
    ],
  },
];
